import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useServiceFormList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refPendingServiceFormsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'customerName',
      label: 'Applicant',
      sortable: true,
    },
    {
      key: 'agentName',
      label: 'Agent',
      sortable: true,
    },
    // {
    //   key: 'paymentRemarks',
    //   label: 'Payment details',
    //   sortable: true,
    // },
    {
      key: 'createdAt',
      label: 'Created By',
      sortable: true,
    },
    {
      key: 'dutyOfficer.name',
      label: 'Duty officer',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalServiceForms = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const paymentStatusFilter = ref([])
  if (Array.isArray(route.query.paymentStatus)) {
    paymentStatusFilter.value = route.query.paymentStatus
  } else if (route.query.paymentStatus) {
    paymentStatusFilter.value = [route.query.paymentStatus]
  }
  const dutyOfficerFilter = ref([])
  if (Array.isArray(route.query.dutyOfficer)) {
    dutyOfficerFilter.value = route.query.dutyOfficer
  } else if (route.query.dutyOfficer) {
    dutyOfficerFilter.value = [route.query.dutyOfficer]
  }
  const toDeletedID = ref(null)
  const type = ref('pending')
  const serviceFormData = ref([])
  const createdAtFilter = ref(route.query.createdAt || '')
  const serviceDateFilter = ref(route.query.serviceDate || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refPendingServiceFormsTable.value ? refPendingServiceFormsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalServiceForms.value,
    }
  })

  const refetchData = () => {
    refPendingServiceFormsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, paymentStatusFilter, createdAtFilter, serviceDateFilter, dutyOfficerFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchServiceForms = (ctx, callback) => {
    store
      .dispatch('app-pending-service-forms/fetchServiceForms', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        paymentStatus: paymentStatusFilter.value,
        createdAt: createdAtFilter.value,
        serviceDate: serviceDateFilter.value,
        dutyOfficer: dutyOfficerFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { serviceForms, total } = response.data
        serviceFormData.value = serviceForms
        callback(serviceForms)
        totalServiceForms.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              paymentStatus: paymentStatusFilter.value,
              createdAt: createdAtFilter.value,
              serviceDate: serviceDateFilter.value,
              dutyOfficer: dutyOfficerFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteServiceForm = () => {
    store
      .dispatch('app-pending-service-forms/deleteServiceForm', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveServiceFormStatusVariant = status => {
    if (status === 'completed' || status === 'completed and paid') return 'success'
    if (status === 'draft') return 'secondary'
    if (status === 'pending') return 'warning'
    if (status === 'awaiting approval') return 'danger'
    return 'primary'
  }

  return {
    fetchServiceForms,
    tableColumns,
    perPage,
    currentPage,
    totalServiceForms,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPendingServiceFormsTable,

    resolveServiceFormStatusVariant,
    refetchData,

    toDeletedID,
    deleteServiceForm,
    serviceFormData,

    // Extra Filters
    statusFilter,
    paymentStatusFilter,
    createdAtFilter,
    serviceDateFilter,
    dutyOfficerFilter,
  }
}
